/**
 * EmailTabPane — the CardDetailDrawer's "Email" tab body. Renders one of
 * three views: the multi-email reader (<CardEmailTab>), the synthesised
 * email-from-card view (<EmailFromCard>) for Trello-imported cards, or
 * the link-an-email empty state (which can now open the inline composer
 * to send a NEW email from the card — ARC-1 MR2).
 *
 * Extracted verbatim from CardDetailDrawer.tsx (operator 500-line ceiling).
 * The caller still gates rendering on `safeActiveTab === 'email' && showEmailTab`.
 */
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'

import type { KanbanApi, KanbanEmailAdapter } from '../../../api'
import type { Card } from '../../../types'
import { EmailFromCard, isTrelloMirrorCard } from '../../EmailFromCard'
import { boardUnseenQueryKey } from '../../board-view/useBoardUnseenEmails'
import { CardEmailTab } from '../CardEmailTab'
import { EmailLinkTickCrossSlot } from '../EmailLinkTickCrossSlot'
import { EmailLinkEmptyState } from '../EmailLinkEmptyState'
import { EmailSearchInline } from '../EmailSearchInline'

export interface EmailTabPaneProps {
  card: Card
  api: KanbanApi
  emailAdapter?: KanbanEmailAdapter
  emailLinkedIds: string[]
  isEmailShaped: boolean
  emailTabInitialId: string | null
  onRailTabChange?: (next: 'emails' | 'jobs' | 'card') => void
}

export function EmailTabPane({
  card,
  api,
  emailAdapter,
  emailLinkedIds,
  isEmailShaped,
  emailTabInitialId,
  onRailTabChange,
}: EmailTabPaneProps) {
  const queryClient = useQueryClient()
  // ARC-1 MR2 — inline "new email from this card" composer for cards
  // with NO linked email yet (the linked-email path gets its compose
  // button from EmailThreadReplyBar inside the reader).
  const [composing, setComposing] = useState(false)
  const InlineComposer = emailAdapter?.InlineComposer

  const onEmptyStateComposerSent = () => {
    setComposing(false)
    // The sent copy was linked to the card server-side — refetch the
    // card so the new related email surfaces in the drawer.
    void queryClient.invalidateQueries({ queryKey: ['kanban', 'card', card.id] })
    void queryClient.invalidateQueries({
      queryKey: ['kanban', 'card-linked-email'],
    })
  }

  // ARC-5 U18 — opening the email tab counts as SEEING the linked emails.
  // This pane only mounts while the tab is active (the drawer gates on
  // `safeActiveTab === 'email'`), so a mount IS the "opened" moment.
  // Fire-and-forget: idempotent on the BE, and the board's unseen
  // aggregate is invalidated so the focus set / chips refresh.
  const emailIdsKey = emailLinkedIds.join(',')
  useEffect(() => {
    if (!api.markEmailsSeen || emailLinkedIds.length === 0) return
    api
      .markEmailsSeen(emailLinkedIds, 'CARD_EMAIL_TAB')
      .then(({ new: n }) => {
        if (n > 0) {
          void queryClient.invalidateQueries({
            queryKey: boardUnseenQueryKey(),
          })
        }
      })
      .catch(() => {
        /* best-effort — never block the reader on seen bookkeeping */
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the id SET, not array identity
  }, [emailIdsKey])

  // ARC-1 MR4 — an email was linked from the in-drawer search: refetch
  // the card so `emailLinkedIds` picks up the new link and the reader
  // replaces the empty state in place (no navigation).
  const onSearchLinked = () => {
    void queryClient.invalidateQueries({ queryKey: ['kanban', 'card', card.id] })
    void queryClient.invalidateQueries({
      queryKey: ['kanban', 'card-linked-email'],
    })
    void queryClient.invalidateQueries({ queryKey: ['kanban', 'email-rail'] })
  }

  // In-drawer search needs a server-side searcher + the link endpoint;
  // read-only bundles fall back to the legacy Email Hub deep-link.
  const canSearchInline = Boolean(
    emailAdapter?.listInboxEmails && api.linkEmailToCard,
  )
  const trelloMirror = isTrelloMirrorCard(card)
  const fetchTrelloDetail = trelloMirror
    ? api.fetchTrelloCardDetail?.bind(api)
    : undefined

  return (
    <div
      role="tabpanel"
      id="card-detail-tabpane-email"
      className="flex-1 min-h-0 flex flex-col"
    >
      {/* qb!3096 — operator tick / cross on the autonomously-
          written Trello-card → Email link. */}
      <EmailLinkTickCrossSlot
        provider={card.provider}
        externalId={card.external_id ?? null}
        confirmFn={api.confirmTrelloEmailLink?.bind(api)}
        rejectFn={api.rejectTrelloEmailLink?.bind(api)}
        fetchDetail={fetchTrelloDetail}
      />
      {emailLinkedIds.length > 0 && emailAdapter ? (
        // Multi-email aware: lists every linked email in a selector and renders
        // the full reader for the selected one. A single-email card shows just
        // the reader.
        <CardEmailTab
          emailIds={emailLinkedIds}
          adapter={emailAdapter}
          fallbackSubject={card.title}
          initialSelectedId={emailTabInitialId}
          cardId={card.id}
          api={api}
          organisationId={card.organisation}
        />
      ) : isEmailShaped ? (
        // ARC-1 contract — the synthesised view stays (845 active prod cards
        // have NO Email row; their content exists only as Trello imports, so
        // the shared reader has nothing to render). But its Email Hub
        // redirect is now fallback-only: when in-drawer search is available,
        // the operator links the real inbox email IN PLACE, which flips the
        // card onto the shared <CardEmailTab> reader permanently.
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1">
            <EmailFromCard
              card={{
                id: card.id,
                title: card.title,
                summary: card.summary,
                primary_email: (card.primary_email ?? null) as string | null,
                updated_at: card.updated_at,
                created_at: card.created_at,
                provider: card.provider,
                // v3 (2026-05-20): pass external_id through so <EmailFromCard>
                // can fetch the Trello commentCard thread. Job-pipeline
                // cards also have an external_id; the provider gate keeps
                // those off /api/trello/cards/.
                external_id: card.external_id ?? null,
              }}
              fetchTrelloCardDetail={fetchTrelloDetail}
              buildSearchHubUrl={
                // Deep-link to the Email Hub's search results for this sender —
                // FALLBACK ONLY for read-only bundles without in-drawer search.
                // (IIFE captures the optional method into a const so TS keeps
                // the narrowing inside the closure.)
                (() => {
                  if (canSearchInline) return undefined
                  const build = emailAdapter?.buildEmailHubUrl?.bind(emailAdapter)
                  return build
                    ? (sender: { addr: string }) =>
                        build(`?from=${encodeURIComponent(sender.addr)}`)
                    : undefined
                })()
              }
            />
          </div>
          {canSearchInline && emailAdapter && (
            <div
              data-testid="email-from-card-link-strip"
              className="border-t border-neutral-200 px-3 py-2 dark:border-zinc-800"
            >
              <p className="mb-1 text-[10.5px] font-medium uppercase tracking-wide text-neutral-500 dark:text-zinc-400">
                Link the original inbox email
              </p>
              <EmailSearchInline
                cardId={card.id}
                adapter={emailAdapter}
                api={api}
                onLinked={onSearchLinked}
              />
            </div>
          )}
        </div>
      ) : composing && InlineComposer ? (
        <div
          data-testid="email-tab-empty-composer"
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="flex items-center justify-between border-b border-neutral-100 px-3 py-1.5 dark:border-zinc-800">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-zinc-400">
              New email
            </span>
            <button
              type="button"
              onClick={() => setComposing(false)}
              aria-label="Close composer"
              className="rounded p-1 text-neutral-500 hover:bg-neutral-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <InlineComposer
              mode="new"
              emailId={null}
              cardId={card.id}
              onSent={onEmptyStateComposerSent}
              onClose={() => setComposing(false)}
            />
          </div>
        </div>
      ) : (
        <EmailLinkEmptyState
          onBrowseEmails={
            onRailTabChange ? () => onRailTabChange('emails') : undefined
          }
          searchUrl={(() => {
            const build = emailAdapter?.buildEmailHubUrl?.bind(emailAdapter)
            const term = (card.title || '').trim()
            return build && term
              ? build(`?search=${encodeURIComponent(term)}`)
              : null
          })()}
          onCompose={InlineComposer ? () => setComposing(true) : undefined}
          searchSlot={
            canSearchInline && emailAdapter ? (
              <EmailSearchInline
                cardId={card.id}
                adapter={emailAdapter}
                api={api}
                onLinked={onSearchLinked}
              />
            ) : undefined
          }
        />
      )}
    </div>
  )
}
